import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const slides = [
  {
    id: 1,
    title: 'Tailoring is our art. You are our canvas',
    image: '/media/site/hero-1.jpg',
   },
  {
    id: 2,
    title: 'Custom Tailoring, Endless Possibilities',
    image: '/media/site/hero-2.jpg',
      },
  {
    id: 3,
    title: 'Tailored For You, Designed by You',
    image: '/media/site/hero-3.jpg',
  },
];

function HeroButton({ children, onClick, className = '', variant = 'filled' }) {
  const baseStyles =
    'relative overflow-hidden font-body text-xs md:text-sm uppercase tracking-[0.2em] md:tracking-[0.25em] px-6 md:px-8 py-3 md:py-4 rounded-full transition-all duration-300 group/btn z-10 cursor-pointer flex items-center justify-center gap-2';

  const variants = {
    filled: 'text-white bg-black border border-white hover:text-black',
    outline: 'text-white bg-transparent border border-white hover:text-black',
    ghost: 'text-white bg-transparent border-none hover:text-[#D4AF37]',
  };

  const fillColors = {
    filled: 'bg-[#D4AF37]',
    outline: 'bg-[#D4AF37]',
    ghost: 'bg-transparent',
  };

  return (
    <button onClick={onClick} className={`${baseStyles} ${variants[variant]} ${className}`}>
      <span className="relative z-10 flex items-center gap-2">{children}</span>
      {variant !== 'ghost' && (
        <span
          className={`absolute inset-0 ${fillColors[variant]} scale-x-0 group-hover/btn:scale-x-100 origin-left transition-transform duration-500 ease-out`}
        ></span>
      )}
    </button>
  );
}

function ArrowIcon({ direction }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {direction === 'left' ? (
        <path
          d="M15 18L9 12L15 6"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ) : (
        <path
          d="M9 6L15 12L9 18"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
    </svg>
  );
}

export default function Hero({ scrollToElement } = {}) {
  const [current, setCurrent] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const navigate = useNavigate();

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 7000);
    return () => clearInterval(timer);
  }, []);

  const goTo = (index) => {
    if (index === current) return;
    setCurrent(index);
  };

  const next = () => setCurrent((prev) => (prev + 1) % slides.length);
  const prev = () => setCurrent((prev) => (prev - 1 + slides.length) % slides.length);

  const activeSlide = slides[current];

  return (
    <div className="w-full h-screen supports-[height:100dvh]:h-[100dvh] bg-black text-white hero-compact">
      <style>{`
        .hero-fade { animation: heroFadeIn 0.9s ease-out; }
        .fade-headline { animation: heroFadeIn 0.9s ease-out; animation-delay: 0.05s; animation-fill-mode: both; }
        .fade-buttons { animation: heroFadeIn 0.9s ease-out; animation-delay: 0.3s; animation-fill-mode: both; }
        .fade-brand { animation: heroFadeIn 0.9s ease-out; animation-delay: 0.45s; animation-fill-mode: both; }
        @keyframes heroFadeIn {
          0% { opacity: 0; transform: translateY(18px); }
          100% { opacity: 1; transform: translateY(0); }
        }

        @media (max-height: 720px) {
          .hero-compact .hero-content {
            padding-bottom: 90px !important;
            justify-content: center !important;
          }
          .hero-compact .hero-headline {
            font-size: clamp(24px, 4vw, 48px) !important;
            line-height: 1.1 !important;
            margin-bottom: 16px !important;
          }
          .hero-compact .hero-brand {
            margin-top: 20px !important;
            font-size: 16px !important;
          }
          .hero-compact .hero-controls {
            bottom: 20px !important;
          }
        }
      `}</style>

      <section
        className="relative w-full h-full flex items-center overflow-hidden"
        style={{
          backgroundImage: `url(${activeSlide.image})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="hero-content relative z-10 h-full w-full max-w-[98%] xl:max-w-[1600px] mx-auto px-4 md:px-6 lg:px-8 flex flex-col justify-start pt-[28vh] md:pt-[42vh] pb-20">
          <div className="max-w-3xl hero-fade w-full text-left flex flex-col h-full">
            <p className="font-body text-[10px] md:text-xs tracking-[0.3em] md:tracking-[0.45em] uppercase text-[#D4AF37]/90 mb-3 md:mb-6 pl-[2px] md:pl-1">
              India's First Custom Tailoring Experience
            </p>

            <h1 className="hero-headline font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-medium leading-[1.1] fade-headline text-white drop-shadow-sm">
              {activeSlide.title}
            </h1>

            <div className="mt-8 md:mt-12 flex flex-wrap gap-4 md:gap-6 fade-buttons">
              <HeroButton
                onClick={() => {
                  navigate('/custom/suit/select_cloth/personalize');
                }}
                variant="filled"
                className="px-6 md:px-10 py-3 md:py-4 text-sm md:text-base"
              >
                Customize
              </HeroButton>
              <HeroButton
                onClick={() => {
                  if (scrollToElement) {
                    scrollToElement('explore');
                    return;
                  }
                  const el = document.getElementById('explore');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }}
                variant="outline"
                className="px-6 md:px-10 py-3 md:py-4 text-sm md:text-base"
              >
                Explore
              </HeroButton>
            </div>

            <p className="hero-brand font-heading italic text-base md:text-xl lg:text-2xl text-[#D4AF37]/90 mt-8 md:mt-12 max-w-2xl fade-brand leading-relaxed">
              {activeSlide.brandLine}
            </p>
          </div>
        </div>

        <div className="hero-controls absolute inset-x-0 bottom-10 z-20 flex items-center justify-between px-3.5 md:px-6 lg:px-8 w-full max-w-[98%] xl:max-w-[1600px] mx-auto">
          <div className="flex items-center gap-4">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                onClick={() => goTo(index)}
                className="group flex flex-col items-start bg-transparent border-none p-0 focus:outline-none"
              >
                <span
                  className={`h-[2px] w-12 sm:w-20 rounded-full transition-all duration-300 ${
                    current === index
                      ? 'bg-[#D4AF37] scale-x-100'
                      : 'bg-white/30 scale-x-75 group-hover:bg-white/60'
                  }`}
                />
                <span
                  className={`mt-2 text-[11px] font-body uppercase tracking-[0.25em] font-medium transition-colors duration-300 ${
                    current === index
                      ? 'text-[#D4AF37]'
                      : 'text-slate-300/70 group-hover:text-slate-100'
                  }`}
                >
                  0{index + 1}
                </span>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-4">
            <button
              onClick={prev}
              className="h-10 w-10 flex items-center justify-center rounded-full border-[1px] border-white/30 bg-black/40 text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition"
              aria-label="Previous slide"
            >
              <ArrowIcon direction="left" />
            </button>
            <button
              onClick={next}
              className="h-10 w-10 flex items-center justify-center rounded-full border-[1px] border-white/30 bg-black/40 text-white hover:border-[#D4AF37] hover:text-[#D4AF37] transition"
              aria-label="Next slide"
            >
              <ArrowIcon direction="right" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
